import React, { useEffect, useState, useMemo } from 'react';
import { Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { usePermisos } from '../hooks/usePermisos';
import WebSocketIndicator from './WebSocketIndicator';
import NotificationBell from './NotificationBell';
import { GlobalSearch } from './GlobalSearch';
import { ThemeToggle } from './ThemeToggle';
import { ProtectedRoute } from './ProtectedRoute';
import ErrorBoundary from './ErrorBoundary';
import api from '../utils/axios';
import { Button } from './ui/button';
import {
  LayoutDashboard,
  AlertTriangle,
  Activity,
  Database,
  Users,
  FileText,
  LogOut,
  Menu,
  X,
  UserCog,
  BarChart3,
  Shield,
  Calendar,
  ShieldOff,
  Clock,
  FileStack,
  ClipboardList,
  LineChart,
  Briefcase,
  Network,
  MapPin,
  Home,
  ChevronLeft,
  Box,
  Settings,
  Columns,
  LayoutGrid,
  GitBranch,
  UserPlus,
  UserCheck,
  History,
  Bell,
  Building2,
  CheckCircle,
  Inbox,
  Search,
  FileSearch,
  ChevronDown,
  ChevronRight,
  Folder,
  FolderOpen,
  Lock,
  HelpCircle,
  Book,
  Palette,
  TrendingUp,
  Download,
  Gift,
  ShieldBan,
  BookOpen,
  Banknote,
  PiggyBank,
  LifeBuoy,
  ArrowRightLeft,
  Gauge,
  Radar,
  FileDown
} from 'lucide-react';

// Map icon names to Lucide components
const iconMap = {
  LayoutDashboard,
  AlertTriangle,
  Activity,
  Database,
  Users,
  FileText,
  UserCog,
  BarChart3,
  Columns,
  Shield,
  Calendar,
  ShieldOff,
  Clock,
  FileStack,
  ClipboardList,
  LineChart,
  Briefcase,
  Network,
  MapPin,
  Settings,
  Box,
  LayoutGrid,
  GitBranch,
  UserPlus,
  UserCheck,
  History,
  Bell,
  Building2,
  LogOut,
  Menu,
  X,
  CheckCircle,
  Inbox,
  Search,
  FileSearch,
  ChevronDown,
  ChevronRight,
  Folder,
  FolderOpen,
  Lock,
  Palette,
  TrendingUp,
  Download,
  Gift,
  ShieldBan,
  BookOpen,
  Banknote,
  PiggyBank,
  LifeBuoy,
  ArrowRightLeft,
  Gauge,
  Radar,
  FileDown
};

// Deriva el slug canónico de un nombre de módulo (misma normalización que ModuleHome).
const getCanonicalSlug = (nombre) => (nombre || '')
  .toLowerCase()
  .replace(/ /g, '-')
  .replace(/ó/g, 'o').replace(/á/g, 'a').replace(/é/g, 'e').replace(/í/g, 'i').replace(/ú/g, 'u').replace(/ñ/g, 'n');

const ModuleSidebar = ({ module, sections, permisosModulo, collapsed, onToggleCollapse, moduleSlug }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = React.useState(false);
  const [expandedGroups, setExpandedGroups] = React.useState({});
  const [brandingName, setBrandingName] = React.useState('AlertGuard');

  React.useEffect(() => {
    api.get('/branding/public').then(r => {
      if (r.data?.nombre_empresa) setBrandingName(r.data.nombre_empresa);
    }).catch(() => {});
  }, []);

  const toggleExpandedGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const goHome = () => {
    navigate('/');
  };

  // Navega SIEMPRE dentro del módulo actual (mantiene ESTE sidebar aunque la
  // sección "viva" en otro módulo — soporta links cruzados entre módulos).
  const seccionPath = (ruta) => {
    const slug = moduleSlug || location.pathname.split('/')[2] || '';
    const sub = (ruta || '').replace(/^\/m\/[^/]+\//, '');
    return `/m/${slug}/${sub}`;
  };

  const getIcon = (iconName) => {
    return iconMap[iconName] || Box;
  };

  // Inline styles - Improved contrast for better visibility
  const sidebarBg = { backgroundColor: '#0f172a' };
  const textWhite = { color: '#ffffff' };
  const textSlate200 = { color: '#e2e8f0' }; // Lighter for better contrast
  const textSlate400 = { color: '#94a3b8' };
  const borderSlate800 = { borderColor: '#1e293b' };
  const activeBtnBg = { backgroundColor: '#2563eb', color: '#ffffff' };
  const logoutBtnStyle = { backgroundColor: '#1e293b', borderColor: '#334155', color: '#ffffff' };
  const menuItemStyle = { color: '#e2e8f0' }; // Better visibility for menu items

  return (
    <>
      {/* Mobile menu button */}
      <button
        data-testid="mobile-menu-toggle"
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md"
        style={{ backgroundColor: '#0f172a', color: '#ffffff' }}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <div
        className={`
          fixed lg:static inset-y-0 left-0 z-40
          flex flex-col
          transform transition-all duration-200 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${collapsed ? 'w-16' : 'w-64'}
        `}
        style={sidebarBg}
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Header with back button */}
          <div className="p-4 border-b" style={{ ...borderSlate800, ...sidebarBg }}>
            {!collapsed && (
              <button
                onClick={goHome}
                className="flex items-center space-x-2 text-slate-400 hover:text-white transition-colors mb-3"
                data-testid="back-to-home-btn"
              >
                <ChevronLeft size={18} />
                <span className="text-sm">Volver al inicio</span>
              </button>
            )}
            <div className={`flex items-center ${collapsed ? 'justify-center' : 'space-x-3'}`}>
              <div 
                className="p-2 rounded-lg flex-shrink-0 cursor-pointer"
                style={{ backgroundColor: module?.color || '#3b82f6' }}
                onClick={collapsed ? goHome : undefined}
                title={collapsed ? 'Volver al inicio' : undefined}
              >
                {React.createElement(getIcon(module?.icono), { 
                  size: 20, 
                  style: textWhite 
                })}
              </div>
              {!collapsed && (
                <div>
                  <h1 className="text-base font-bold tracking-tight" style={textWhite}>
                    {module?.nombre || 'Módulo'}
                  </h1>
                  <p className="text-xs" style={textSlate400}>{brandingName}</p>
                </div>
              )}
            </div>
          </div>

          {/* Collapse toggle button */}
          <button
            onClick={onToggleCollapse}
            className="hidden lg:flex items-center justify-center py-1.5 border-b hover:bg-slate-800 transition-colors"
            style={{ ...borderSlate800, color: '#94a3b8' }}
            data-testid="sidebar-collapse-btn"
            title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>

          {/* Navigation */}
          <nav className={`flex-1 ${collapsed ? 'p-2' : 'p-4'} space-y-1 overflow-y-auto min-h-0`} style={sidebarBg}>
            {sections
              .filter(s => !s.grupo_padre_id && !s.es_recuadro) // Nivel superior; los recuadros no van en el menú
              .map((section) => {
              const Icon = getIcon(section.icono);
              const isActive = location.pathname === seccionPath(section.ruta);
              
              // Si es un grupo, renderizar con hijos
              if (section.es_grupo) {
                const childSections = sections.filter(s => s.grupo_padre_id === section.id);
                const isGroupExpanded = expandedGroups[section.id];
                const hasActiveChild = childSections.some(c => location.pathname === seccionPath(c.ruta));
                
                if (collapsed) {
                  // Collapsed: show group icon, click navigates to first child
                  const firstChild = childSections[0];
                  return (
                    <button
                      key={section.id}
                      onClick={() => { if (firstChild) navigate(seccionPath(firstChild.ruta)); }}
                      className={`w-full flex items-center justify-center p-2.5 rounded-md transition-all duration-200 ${
                        hasActiveChild ? 'bg-blue-600 text-white' : 'text-slate-200 hover:bg-slate-800 hover:text-white'
                      }`}
                      title={section.nombre}
                    >
                      <Icon size={20} />
                    </button>
                  );
                }
                
                return (
                  <div key={section.id}>
                    <button
                      onClick={() => toggleExpandedGroup(section.id)}
                      className={`w-full flex items-center justify-between px-4 py-2.5 rounded-md transition-all duration-200 ${
                        hasActiveChild 
                          ? 'bg-slate-800 text-white' 
                          : 'text-slate-200 hover:bg-slate-800 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon size={20} className="text-slate-200" />
                        <span className="text-sm font-medium">{section.nombre}</span>
                      </div>
                      {isGroupExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    {isGroupExpanded && childSections.length > 0 && (
                      <div className="ml-4 mt-1 space-y-1 border-l border-slate-700 pl-2">
                        {childSections.map(child => {
                          const ChildIcon = getIcon(child.icono);
                          const isChildActive = location.pathname === seccionPath(child.ruta);
                          return (
                            <button
                              key={child.id}
                              onClick={() => {
                                navigate(seccionPath(child.ruta));
                                setIsOpen(false);
                              }}
                              className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md transition-all duration-200 ${
                                isChildActive 
                                  ? 'bg-blue-600 text-white' 
                                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                              }`}
                            >
                              <ChildIcon size={16} className={isChildActive ? 'text-white' : 'text-slate-300'} />
                              <span className="text-xs font-medium">{child.nombre}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              if (collapsed) {
                return (
                  <button
                    key={section.id}
                    data-testid={`nav-${section.key}`}
                    onClick={() => { navigate(seccionPath(section.ruta)); setIsOpen(false); }}
                    className={`w-full flex items-center justify-center p-2.5 rounded-md transition-all duration-200 ${
                      isActive ? 'bg-blue-600 text-white' : 'text-slate-200 hover:bg-slate-800 hover:text-white'
                    }`}
                    title={section.nombre}
                  >
                    <Icon size={20} className={isActive ? 'text-white' : 'text-slate-200'} />
                  </button>
                );
              }

              return (
                <button
                  key={section.id}
                  data-testid={`nav-${section.key}`}
                  onClick={() => {
                    navigate(seccionPath(section.ruta));
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-md transition-all duration-200 ${
                    isActive 
                      ? 'bg-blue-600 text-white' 
                      : 'text-slate-200 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <Icon size={20} className={isActive ? 'text-white' : 'text-slate-200'} />
                  <span className="text-sm font-medium">{section.nombre}</span>
                </button>
              );
            })}
          </nav>

          {/* User info & logout */}
          <div className={`${collapsed ? 'p-2' : 'p-4'} border-t`} style={{ ...borderSlate800, ...sidebarBg }}>
            {collapsed ? (
              <div className="flex flex-col items-center gap-2">
                <div 
                  className="w-9 h-9 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#2563eb' }}
                  title={user?.nombre}
                >
                  <span className="text-xs font-bold" style={textWhite}>
                    {user?.nombre?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <button
                  data-testid="logout-button"
                  onClick={handleLogout}
                  className="p-2 rounded-md hover:bg-slate-800 transition-colors"
                  title="Cerrar Sesión"
                  style={{ color: '#94a3b8' }}
                >
                  <LogOut size={16} />
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center space-x-3 mb-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: '#2563eb' }}
                  >
                    <span className="text-sm font-bold" style={textWhite}>
                      {user?.nombre?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={textWhite}>{user?.nombre}</p>
                    <p className="text-xs truncate" style={textSlate400}>{user?.email}</p>
                  </div>
                </div>
                <Button
                  data-testid="logout-button"
                  onClick={handleLogout}
                  variant="outline"
                  size="sm"
                  className="w-full"
                  style={logoutBtnStyle}
                >
                  <LogOut size={16} className="mr-2" style={textWhite} />
                  <span style={textWhite}>Cerrar Sesión</span>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export const ModuleLayout = () => {
  const { moduleSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isConnected } = useWebSocket(user?.id);
  const { loadPermisosModulo, filterSeccionesConPermiso, isAdmin } = usePermisos();
  const [module, setModule] = useState(null);
  const [sections, setSections] = useState([]);
  const [allSections, setAllSections] = useState([]);
  const [permisosModulo, setPermisosModulo] = useState({});
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebar_collapsed', String(next));
      return next;
    });
  };

  useEffect(() => {
    if (moduleSlug) {
      loadModule();
    }
  }, [moduleSlug]);

  const loadModule = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/modules/by-slug/${moduleSlug}`);

      // Canonizar URLs con slug legacy/alias (ej. /m/credito/* -> /m/prestamos/*).
      // Sin esto, currentSection queda null y ProtectedRoute no puede aplicar el permiso
      // granular de la sección (bypass de RBAC en la ruta legacy).
      // IMPORTANTE: NO bajamos `loading` al redirigir: mantenemos el spinner hasta que la
      // carga canónica termine, para que el <Outlet/> NO se monte con module/currentSection
      // en null (evita que la pantalla protegida dispare su request durante la transición).
      const canonicalSlug = getCanonicalSlug(res.data.nombre);
      if (canonicalSlug && canonicalSlug !== moduleSlug) {
        const rest = location.pathname.slice(`/m/${moduleSlug}`.length);
        navigate(`/m/${canonicalSlug}${rest}${location.search}`, { replace: true });
        return; // el cambio de moduleSlug re-dispara loadModule; loading sigue en true
      }

      setModule(res.data);
      setAllSections(res.data.secciones || []);
      
      // Cargar permisos para este módulo
      const permisos = await loadPermisosModulo(res.data.id);
      setPermisosModulo(permisos);
      
      // Filtrar secciones según permisos
      const seccionesFiltradas = filterSeccionesConPermiso(res.data.secciones || [], permisos);
      setSections(seccionesFiltradas);
      setLoading(false);
    } catch (error) {
      console.error('Error loading module:', error);
      // If module not found, redirect to home
      navigate('/');
      setLoading(false);
    }
  };

  // Obtener la sección actual basada en la ruta
  const currentSection = useMemo(() => {
    if (!allSections.length) return null;
    
    // Buscar la sección que coincida con la ruta actual (comparando por sub-ruta,
    // sin el prefijo /m/<modulo>/, para soportar la misma sección en otro módulo).
    const currentSub = location.pathname.replace(/^\/m\/[^/]+\/?/, '');
    return allSections.find(section => (section.ruta || '').replace(/^\/m\/[^/]+\//, '') === currentSub);
  }, [location.pathname, allSections]);

  // Redirigir a la primera sección si estamos en la raíz del módulo
  useEffect(() => {
    if (!loading && sections.length > 0 && module) {
      const currentPath = location.pathname;
      const moduleBasePath = `/m/${moduleSlug}`;
      
      // Si estamos exactamente en la ruta base del módulo (sin sección)
      if (currentPath === moduleBasePath || currentPath === `${moduleBasePath}/`) {
        const toMod = (ruta) => `/m/${moduleSlug}/` + (ruta || '').replace(/^\/m\/[^/]+\//, '');
        const miTablero = sections.find((s) => s.key === 'kpis-mi-tablero');
        // Primera sección REALMENTE navegable a la que el usuario tiene acceso:
        // excluye recuadros (bloques sin menú) y grupos contenedores (sin ruta propia).
        const firstSection = sections.find((s) => !s.es_recuadro && !s.es_grupo && s.ruta)
          || sections.find((s) => s.ruta)
          || sections[0];
        if (miTablero) {
          // Módulo KPIs: ir a "Mi Tablero" si el usuario ya creó uno propio; si no, a Inicio KPIs.
          api.get('/kpis/dashboard/config')
            .then((r) => {
              const target = r.data?.personal ? miTablero.ruta : (firstSection?.ruta || miTablero.ruta);
              navigate(toMod(target), { replace: true });
            })
            .catch(() => { if (firstSection?.ruta) navigate(toMod(firstSection.ruta), { replace: true }); });
        } else if (firstSection && firstSection.ruta) {
          navigate(toMod(firstSection.ruta), { replace: true });
        }
      }
    }
  }, [loading, sections, module, location.pathname, moduleSlug, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando módulo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-slate-900">
      <ModuleSidebar module={module} sections={sections} permisosModulo={permisosModulo} collapsed={sidebarCollapsed} onToggleCollapse={toggleSidebar} moduleSlug={moduleSlug} />
      <main className="flex-1 min-w-0 overflow-hidden bg-slate-50 dark:bg-slate-900 relative flex flex-col">
        {/* Top bar with Help button, WebSocket status and Notification Bell */}
        <div className="fixed top-4 right-6 z-50 flex items-center gap-2 pointer-events-none">
          <div className="pointer-events-auto"><GlobalSearch /></div>
          <div className="pointer-events-auto"><ThemeToggle /></div>
          <div className="pointer-events-auto"><WebSocketIndicator isConnected={isConnected} /></div>
          <div className="pointer-events-auto"><NotificationBell /></div>
          <div className="pointer-events-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/manual')}
              className="bg-blue-600 hover:bg-blue-700 shadow-lg border-0 text-white"
              title="Manual de Usuario"
              data-testid="help-button"
            >
              <HelpCircle size={16} className="text-white" />
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <ProtectedRoute
            moduloId={module?.id}
            seccionId={currentSection?.id}
            requiredPermission="ver"
            fallbackPath="/"
          >
            <ErrorBoundary>
              <Outlet context={{ module, sections, permisosModulo }} />
            </ErrorBoundary>
          </ProtectedRoute>
        </div>
      </main>
    </div>
  );
};

export default ModuleLayout;
